import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Users, 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Search,
  Shield,
  UserCheck,
  UserCog
} from 'lucide-react';
import { toast } from 'sonner';

export default function UsersPage() {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'operador',
    company_id: ''
  });

  // Verificar permissões do usuário logado
  const isAdminMaster = currentUser?.role === 'admin_master';
  const isGestor = currentUser?.role === 'gestor' || isAdminMaster;
  const canCreateUsers = isGestor;
  const canDeleteUsers = isGestor;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes] = await Promise.all([
        api.get('/users')
      ]);
      setUsers(usersRes.data);
      
      // Só busca empresas se for admin_master
      if (isAdminMaster) {
        const companiesRes = await api.get('/companies');
        setCompanies(companiesRes.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Se for gestor, força a empresa do usuário logado
      const submitData = {...formData};
      if (!isAdminMaster && currentUser?.company_id) {
        submitData.company_id = currentUser.company_id;
      }
      
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, submitData);
        toast.success('Usuário atualizado com sucesso');
      } else {
        await api.post('/auth/register', submitData);
        toast.success('Usuário criado com sucesso');
      }
      
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Tem certeza que deseja desativar este usuário?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Usuário desativado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao desativar usuário');
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      company_id: user.company_id || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'operador',
      company_id: ''
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin_master': return <Shield size={16} className="text-purple-600" />;
      case 'gestor': return <UserCog size={16} className="text-blue-600" />;
      default: return <UserCheck size={16} className="text-slate-600" />;
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin_master: 'bg-purple-100 text-purple-800 border-purple-200',
      gestor: 'bg-blue-100 text-blue-800 border-blue-200',
      operador: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    const labels = {
      admin_master: 'Admin Master',
      gestor: 'Gestor',
      operador: 'Operador'
    };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[role]}`}>
        {getRoleIcon(role)}
        {labels[role]}
      </span>
    );
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 md:ml-64 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a365d]" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-6 md:p-8 lg:p-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-[#1a365d]" data-testid="users-title">
              <Users className="inline mr-3" size={32} />
              Gestão de Usuários
            </h1>
            <p className="text-slate-500 mt-1">
              {isAdminMaster 
                ? 'Gerencie todos os usuários do sistema' 
                : isGestor 
                  ? 'Gerencie os usuários da sua empresa' 
                  : 'Visualize e edite seu perfil'}
            </p>
          </div>
          
          {canCreateUsers && (
            <Button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              data-testid="add-user-button"
            >
              <Plus className="mr-2" size={18} />
              Novo Usuário
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="mb-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nome ou email..."
            className="pl-10"
            data-testid="search-users-input"
          />
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Nome</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Nível</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Empresa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Criado em</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      Nenhum usuário encontrado
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-800">
                        {user.name}
                        {user.id === currentUser?.id && (
                          <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">(você)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {user.email}
                      </td>
                      <td className="px-6 py-4">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {isAdminMaster 
                          ? (companies.find(c => c.id === user.company_id)?.name || '-')
                          : (user.company_id ? 'Minha Empresa' : '-')
                        }
                      </td>
                      <td className="px-6 py-4 text-slate-500 text-sm">
                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditModal(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          {canDeleteUsers && user.id !== currentUser?.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(user.id)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* User Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </DialogTitle>
              <DialogDescription>
                {editingUser ? 'Atualize os dados do usuário' : 'Preencha os dados para criar um novo usuário'}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome Completo</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Nome do usuário"
                  required
                  data-testid="user-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@exemplo.com"
                  required
                  data-testid="user-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="password">
                  Senha {editingUser && '(deixe em branco para manter)'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                  required={!editingUser}
                  data-testid="user-password-input"
                />
              </div>
              
              {/* Nível de acesso - só aparece para quem pode alterar */}
              {isGestor && (
                <div>
                  <Label htmlFor="role">Nível de Acesso</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({...formData, role: value})}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue placeholder="Selecione o nível" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operador">Operador</SelectItem>
                      <SelectItem value="gestor">Gestor</SelectItem>
                      {isAdminMaster && <SelectItem value="admin_master">Admin Master</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Empresa - só aparece para admin_master */}
              {isAdminMaster && (
                <div>
                  <Label htmlFor="company">Empresa</Label>
                  <Select
                    value={formData.company_id || 'none'}
                    onValueChange={(value) => setFormData({...formData, company_id: value === 'none' ? '' : value})}
                  >
                  <SelectTrigger data-testid="user-company-select">
                    <SelectValue placeholder="Selecione a empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma (Admin Master)</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
              
              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#1a365d] hover:bg-[#1a365d]/90" data-testid="save-user-button">
                  {editingUser ? 'Atualizar' : 'Criar Usuário'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
